import { datadogObservability as o11yTracker } from '~/lib/services/observability/datadog';
import type { Observability } from '~/lib/services/observability/types';

/**
 * Observability plugin for Nuxt
 * Initializes the observability service (Datadog RUM by default)
 * Makes it available throughout the application via $o11y
 *
 * Usage in components:
 *   - Via composable: const { $o11y } = useNuxtApp()
 */
export default defineNuxtPlugin({
  name: 'observability',
  setup(nuxtApp) {
    const config = useRuntimeConfig();
    const userStore = useUserStore();
    const { locale } = nuxtApp.$i18n;
    const bffGatewayUrl = config.public.bffGatewayBaseUrl ?? '';
    const observabilityConfig = config.public.observability;

    function setupUserTracking() {
      watchEffect(() => {
        const userContext = userStore.user
          ? {
              id: userStore.user.id,
              email: userStore.user.email,
              name: userStore.displayName,
              currency: userStore.user.currency,
              gender: userStore.user.gender,
              locale: locale.value,
            }
          : null;
        o11yTracker.setUser(userContext);
      });
    }

    o11yTracker.init({
      enabled: observabilityConfig.enabled !== false,
      environment: observabilityConfig.environment,
      service: observabilityConfig.service,
      version: observabilityConfig.version,
      applicationId: observabilityConfig.applicationId,
      clientToken: observabilityConfig.clientToken,
      site: observabilityConfig.site,
      tracingUrls: [bffGatewayUrl],
      trackingRate: Number(observabilityConfig.trackingRate) || 100,
      replayRate: Number(observabilityConfig.replayRate) || 20,
    });

    if (o11yTracker.isReady) {
      setupUserTracking();
      o11yTracker.setTenant(config.public.tenantId);
    }

    return {
      provide: {
        o11y: o11yTracker,
      },
    };
  },
});

declare module '#app' {
  interface NuxtApp {
    $o11y: Observability<unknown>;
  }
}

declare module 'vue' {
  interface ComponentCustomProperties {
    $o11y: Observability<unknown>;
  }
}
